import { decodeSelectionToCodes } from './selectionDecoder';

export interface ParsedAppRoute {
  publicViewParams?: { propertyId: string; collectionName: string } | null;
  groupViewParams?: { groupId: string } | null;
  newlyAddedDate?: string | null;
  newlyAddedRange?: { startDate: Date; endDate: Date } | null;
  isClientAccessMode: boolean;
  isClientRegisterMode: boolean;
  isEarthquakeLandingMode: boolean;
  isPublicReferralMode: boolean;
  isSalesProcessMode: boolean;
  locationViewParams?: { city: string; locality: string } | null;
  selectionCodes?: string | null;
}

function getTodayDateStr(): string {
  const now = new Date();
  const d = String(now.getDate()).padStart(2, '0');
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${d}-${m}-${now.getFullYear()}`;
}

function parseNewlyAddedRange(dateStr: string) {
  const [day, month, year] = dateStr.split('-').map(Number);
  const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 7);
  startDate.setHours(0, 0, 0, 0);
  return { startDate, endDate };
}

export function parseAppRoute(): ParsedAppRoute {
  const result: ParsedAppRoute = {
    publicViewParams: null, groupViewParams: null, newlyAddedDate: null,
    newlyAddedRange: null, isClientAccessMode: false, isClientRegisterMode: false,
    isEarthquakeLandingMode: false,
    isPublicReferralMode: false, isSalesProcessMode: false,
    locationViewParams: null, selectionCodes: null,
  };

  const rawPathname = window.location.pathname;
  const pathname = rawPathname.toLowerCase();
  const hash = window.location.hash;

  const isEarthquakeRoute =
    pathname === '/empezar-de-nuevo' || pathname.startsWith('/empezar-de-nuevo/') ||
    pathname === '/empezardenuevo' || pathname.startsWith('/empezardenuevo/') ||
    pathname === '/reactivacion-vivienda' || pathname.startsWith('/reactivacion-vivienda/') ||
    pathname === '/alivio-vivienda' || pathname.startsWith('/alivio-vivienda/') ||
    hash.startsWith('#/empezar-de-nuevo') ||
    hash.startsWith('#/empezardenuevo') ||
    hash.startsWith('#/reactivacion-vivienda') ||
    hash.startsWith('#/alivio-vivienda');

  if (isEarthquakeRoute) {
    if (hash && (hash.startsWith('#/empezar-de-nuevo') || hash.startsWith('#/empezardenuevo') || hash.startsWith('#/reactivacion-vivienda') || hash.startsWith('#/alivio-vivienda'))) {
      window.history.replaceState(null, '', '/empezar-de-nuevo');
    }
    result.isEarthquakeLandingMode = true;
    return result;
  }

  // 1. Pathname-based and hash-based client routes (strictly URL-based)
  const isClientAccessRoute =
    pathname === '/accesocliente' || pathname.startsWith('/accesocliente/') ||
    pathname === '/acceso-cliente' || pathname.startsWith('/acceso-cliente/') ||
    hash.startsWith('#/accesocliente') ||
    hash.startsWith('#/acceso-cliente');

  const isClientRegisterRoute =
    pathname === '/registrocliente' || pathname.startsWith('/registrocliente/') ||
    pathname === '/registro-cliente' || pathname.startsWith('/registro-cliente/') ||
    hash.startsWith('#/registrocliente') ||
    hash.startsWith('#/registro-cliente');

  if (isClientAccessRoute) {
    result.isClientAccessMode = true;
    return result;
  }

  if (isClientRegisterRoute) {
    result.isClientRegisterMode = true;
    return result;
  }

  if (pathname.startsWith('/newly-added')) {
    if (hash) window.history.replaceState(null, '', rawPathname);
    const match = pathname.match(/\/newly-added\/(\d{2}-\d{2}-\d{4})/);
    const dateStr = match ? match[1] : getTodayDateStr();
    result.newlyAddedDate = dateStr;
    result.newlyAddedRange = parseNewlyAddedRange(dateStr);
    return result;
  }

  // Support clean URL /share/:id without hash
  if (pathname.startsWith('/share/')) {
    const match = rawPathname.match(/^\/share\/([a-zA-Z0-9-]+)/i);
    if (match) {
      const searchParams = new URLSearchParams(window.location.search);
      let collectionName = searchParams.get('collection') || 'c';
      if (collectionName.toLowerCase() === 'ciencuadras') collectionName = 'c';
      result.publicViewParams = { propertyId: match[1], collectionName };
      return result;
    }
  }

  // 2. Hash-based routes
  if (hash.startsWith('#/newly-added')) {
    const match = hash.match(/#\/newly-added\/(\d{2}-\d{2}-\d{4})/);
    const dateStr = match ? match[1] : getTodayDateStr();
    result.newlyAddedDate = dateStr;
    result.newlyAddedRange = parseNewlyAddedRange(dateStr);
    return result;
  }

  if (hash.startsWith('#/share/')) {
    const [path, queryString] = hash.substring(2).split('?');
    const shareMatch = path.match(/^share\/([a-zA-Z0-9-]+)/);
    if (shareMatch) {
      let collectionName = new URLSearchParams(queryString || '').get('collection') || 'c';
      if (collectionName.toLowerCase() === 'ciencuadras') collectionName = 'c';
      result.publicViewParams = { propertyId: shareMatch[1], collectionName };
    }
    return result;
  }

  if (hash.startsWith('#/group/')) {
    const groupMatch = hash.substring(2).match(/^group\/([a-zA-Z0-9-_]+)/);
    if (groupMatch) result.groupViewParams = { groupId: groupMatch[1] };
    return result;
  }

  if (hash.startsWith('#/selection/')) {
    const match = hash.substring(2).match(/^selection\/([a-zA-Z0-9-_]+)/);
    if (match) {
      const codes = decodeSelectionToCodes(match[1]);
      if (codes) result.selectionCodes = codes;
    }
    return result;
  }

  if (hash.startsWith('#/ciudades/')) {
    const match = hash.substring(2).match(/^ciudades\/([^/]+)\/localidades\/([^/]+)/);
    if (match) {
      result.locationViewParams = { city: decodeURIComponent(match[1]), locality: decodeURIComponent(match[2]) };
    }
    return result;
  }

  if (hash.startsWith('#/referido-credito')) result.isPublicReferralMode = true;
  if (hash.startsWith('#/proceso-venta')) result.isSalesProcessMode = true;

  return result;
}
